import {authDropdownIcons} from '@app/auth/auth-dropdown-icons';
import {useLogout} from '@common/auth/requests/logout';
import {useAuth} from '@common/auth/use-auth';
import {ColorSchemeContext} from '@common/core/color-scheme-provider';
import {SiteConfigContext} from '@common/core/settings/site-config-context';
import {useCustomMenu} from '@common/menus/use-custom-menu';
import {useNavigate} from '@common/ui/navigation/use-navigate';
import {useActiveWorkspaceId} from '@common/workspace/active-workspace-id-context';
import {useUserWorkspaces} from '@common/workspace/user-workspaces';
import {Placement} from '@floating-ui/react-dom';
import {ListboxItemProps} from '@ui/forms/listbox/item';
import {Trans} from '@ui/i18n/trans';
import {createSvgIconFromTree} from '@ui/icons/create-svg-icon';
import {LucideIcon} from '@ui/icons/lucide/lucide-icon-wrapper';
import {SvgIconProps} from '@ui/icons/svg-icon';
import {Menu, MenuItem, MenuSection, MenuTrigger} from '@ui/menu/menu-trigger';
import {useSettings} from '@ui/settings/use-settings';
import {
  BellIcon,
  CheckIcon,
  CircleUser,
  CreditCardIcon,
  HardDriveIcon,
  LogOutIcon,
  MoonIcon,
  SunIcon,
  UsersIcon,
} from 'lucide-react';
import {cloneElement, ReactElement, use, useContext} from 'react';

interface Props {
  children: ReactElement;
  items?: ReactElement<ListboxItemProps>[];
  placement?: Placement;
  accountSettingsLink?: string;
}
export function NavbarAuthMenu({
  children,
  items,
  placement,
  accountSettingsLink,
}: Props) {
  const {auth} = useContext(SiteConfigContext);
  const logout = useLogout();
  const menu = useCustomMenu('auth-dropdown');
  const {notifications, themes} = useSettings();
  const {user, isSubscribed} = useAuth();
  const navigate = useNavigate();
  const {colorScheme, setColorScheme} = use(ColorSchemeContext);
  const {workspaceId, setWorkspaceId} = useActiveWorkspaceId();
  const {data: workspaces} = useUserWorkspaces();
  if (!user) return null;
  const hasUnreadNotif = !!user.unread_notifications_count;

  const notifMenuItem = (
    <MenuItem
      className="md:hidden"
      value="notifications"
      startIcon={<LucideIcon icon={BellIcon} size="xs" />}
      onSelected={() => {
        navigate('/notifications');
      }}
    >
      <Trans message="Notifications" />
      {hasUnreadNotif ? ` (${user.unread_notifications_count})` : undefined}
    </MenuItem>
  );

  const billingMenuItem = (
    <MenuItem
      value="billing"
      startIcon={<LucideIcon icon={CreditCardIcon} size="xs" />}
      onSelected={() => {
        navigate('/billing');
      }}
    >
      <Trans message="Billing" />
    </MenuItem>
  );

  return (
    <MenuTrigger placement={placement}>
      {children}
      <Menu>
        {menu &&
          menu.items.map(item => {
            let icon: ReactElement<SvgIconProps> | null = null;

            if (item.icon) {
              const IconCmp = createSvgIconFromTree(item.icon);
              icon = IconCmp && <IconCmp size="xs" />;
            } else {
              const IconCmp = authDropdownIcons[item.action.split('?')[0]];
              icon =
                IconCmp &&
                cloneElement(IconCmp, {size: IconCmp.props.size ?? 'xs'});
            }

            let action = item.action;

            if (action === '/account-settings' && accountSettingsLink) {
              action = accountSettingsLink;
            }

            return (
              <MenuItem
                value={item.id}
                key={item.id}
                startIcon={icon}
                onSelected={() => {
                  if (item.type === 'link') {
                    window.open(action, '_blank');
                  } else {
                    navigate(action);
                  }
                }}
              >
                <Trans message={item.label} />
              </MenuItem>
            );
          })}
        {auth?.getUserProfileLink && (
          <MenuItem
            value="profile"
            startIcon={<LucideIcon icon={CircleUser} size="xs" />}
            onSelected={() => {
              navigate(encodeURI(auth.getUserProfileLink!(user)));
            }}
          >
            <Trans message="Profile page" />
          </MenuItem>
        )}
        {items?.map(item => item)}
        {notifications?.integrated ? notifMenuItem : undefined}
        {isSubscribed && billingMenuItem}
        {themes?.user_change && (
          <MenuItem
            value="toggle-color-scheme"
            startIcon={
              colorScheme === 'light' ? (
                <LucideIcon icon={MoonIcon} size="xs" />
              ) : (
                <LucideIcon icon={SunIcon} size="xs" />
              )
            }
            onSelected={() => {
              setColorScheme(colorScheme === 'light' ? 'dark' : 'light');
            }}
          >
            {colorScheme === 'light' ? (
              <Trans message="Dark mode" />
            ) : (
              <Trans message="Light mode" />
            )}
          </MenuItem>
        )}

        <MenuItem
          value="logout"
          startIcon={<LucideIcon icon={LogOutIcon} size="xs" />}
          onSelected={() => {
            logout.mutate();
          }}
        >
            <Trans message="Log out" />
          </MenuItem>

        {workspaces?.length ? (
          <MenuSection label={<Trans message="Drive" />}>
            {workspaces.map(workspace => {
              const isActive = workspaceId === workspace.id;
              return (
                <MenuItem
                  key={workspace.id}
                  value={`workspace-${workspace.id}`}
                  startIcon={
                    isActive ? (
                      <LucideIcon icon={CheckIcon} size="xs" />
                    ) : workspace.default ? (
                      <LucideIcon icon={HardDriveIcon} size="xs" />
                    ) : (
                      <LucideIcon icon={UsersIcon} size="xs" />
                    )
                  }
                  onSelected={() => {
                    setWorkspaceId(workspace.id);
                    navigate('/drive');
                  }}
                >
                  {workspace.default ? (
                    <Trans message="My private drive" />
                  ) : (
                    workspace.name
                  )}
                </MenuItem>
              );
            })}
          </MenuSection>
        ) : null}
      </Menu>
    </MenuTrigger>
  );
}
